<script setup>
import { computed, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Logo from 'next/icon/Logo.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

const props = defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

// Super admin vê todas as suas clínicas numa sanfona inline expandida
// (só faz sentido na sidebar expandida; colapsada mantém o dropdown/logo).
const showAccordion = computed(
  () => isSuperAdmin.value && !props.isCollapsed && !!currentAccount.value.name
);
const isAccordionOpen = ref(false);
const toggleAccordion = () => {
  isAccordionOpen.value = !isAccordionOpen.value;
};

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/clinical-dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <!-- Super admin: sanfona inline expandida com todas as clínicas -->
  <div v-if="showAccordion" class="flex flex-col w-full gap-1">
    <button
      class="flex items-center justify-between w-full px-2 py-1 rounded-lg cursor-pointer hover:bg-n-alpha-1"
      :aria-expanded="isAccordionOpen"
      @click="toggleAccordion"
    >
      <span
        class="text-xs font-medium tracking-wide uppercase truncate text-n-slate-10"
      >
        {{ t('SIDEBAR_ITEMS.SWITCH_ACCOUNT') }}
      </span>
      <span
        aria-hidden="true"
        class="size-4 text-n-slate-10 flex-shrink-0"
        :class="
          isAccordionOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'
        "
      />
    </button>
    <div v-show="isAccordionOpen" class="flex flex-col gap-px">
      <button
        v-for="account in sortedCurrentUserAccounts"
        :key="account.id"
        :data-account-id="account.id"
        class="flex items-center justify-between w-full gap-2 px-2 py-1.5 text-left rounded-lg cursor-pointer rtl:text-right"
        :class="
          account.id === accountId ? 'bg-n-alpha-1' : 'hover:bg-n-alpha-1'
        "
        :title="account.name"
        @click="onChangeAccount(account.id)"
      >
        <span
          class="text-sm leading-5 truncate min-w-0"
          :class="
            account.id === accountId
              ? 'text-n-slate-12 font-medium'
              : 'text-n-slate-11'
          "
        >
          {{ account.name }}
        </span>
        <Icon
          v-show="account.id === accountId"
          icon="i-lucide-check"
          class="flex-shrink-0 text-n-teal-11 size-4"
        />
      </button>
    </div>
  </div>

  <!-- Demais usuários (ou sidebar colapsada): dropdown original -->
  <DropdownContainer v-else>
    <template #trigger="{ toggle, isOpen }">
      <!-- Collapsed view: Logo trigger -->
      <button
        v-if="isCollapsed"
        class="grid flex-shrink-0 place-content-center p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        :title="currentAccount.name"
        @click="toggle"
      >
        <Logo class="size-7" />
      </button>
      <!-- Expanded view: Account name trigger -->
      <button
        v-else
        id="sidebar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-lg px-2"
        :class="[
          isOpen && 'bg-n-alpha-1',
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="() => showAccountSwitcher && toggle()"
      >
        <span
          class="text-sm font-medium leading-5 text-n-slate-12 truncate"
          aria-live="polite"
        >
          {{ currentAccount.name }}
        </span>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-lucide-chevron-down size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody
      v-if="showAccountSwitcher || isCollapsed"
      class="min-w-80 z-50"
    >
      <DropdownSection :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')">
        <DropdownItem
          v-for="account in sortedCurrentUserAccounts"
          :id="`account-${account.id}`"
          :key="account.id"
          class="cursor-pointer"
          @click="onChangeAccount(account.id)"
        >
          <template #label>
            <div
              :for="account.name"
              class="text-left rtl:text-right flex gap-2 items-center"
            >
              <span
                class="text-n-slate-12 max-w-36 truncate min-w-0"
                :title="account.name"
              >
                {{ account.name }}
              </span>
              <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
              <span
                class="text-n-slate-11 max-w-24 truncate capitalize"
                :title="account.name"
              >
                {{
                  account.custom_role_id
                    ? account.custom_role.name
                    : account.role
                }}
              </span>
            </div>
            <Icon
              v-show="account.id === accountId"
              icon="i-lucide-check"
              class="text-n-teal-11 size-5"
            />
          </template>
        </DropdownItem>
      </DropdownSection>
      <DropdownItem v-if="globalConfig.createNewAccountFromDashboard">
        <ButtonNext
          color="slate"
          variant="faded"
          class="w-full"
          size="sm"
          @click="emitNewAccount"
        >
          {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
        </ButtonNext>
      </DropdownItem>
    </DropdownBody>
  </DropdownContainer>
</template>
